import React, { useState, useEffect, useRef } from "react";
import { Layout, Popover, Divider, Button, Tooltip, message } from "antd";
import { Link, useHistory, useLocation } from "react-router-dom";
import CustomScrollbars from "util/CustomScrollbars";
import languageData from "./languageData";
import { switchLanguage, toggleCollapsedSideNav } from "../../appRedux/actions";
import SearchBox from "../../components/SearchBox";
import UserInfo from "../../components/UserInfo";
import AppNotification from "../../components/AppNotification";
import MailNotification from "../../components/MailNotification";
import Auxiliary from "util/Auxiliary";
import UserProfile from "./UserProfile";
import RecentAccessedJob from "./RecentAccessedJob";
import AdvanceSearch from "./AdvanceSearch";
import { useAuth } from 'authentication';

import { NAV_STYLE_DRAWER, NAV_STYLE_FIXED, NAV_STYLE_MINI_SIDEBAR, TAB_SIZE } from "../../constants/ThemeSetting";
import { useDispatch, useSelector } from "react-redux";

const { Header } = Layout;

const IPAD_SIZE = 1024; // iPad的宽度阈值
const MOBILE_SIZE = 768; // 手机的宽度阈值

const Topbar = () => {
  const { locale, navStyle } = useSelector(({ settings }) => settings);
  const navCollapsed = useSelector(({ common }) => common.navCollapsed);
  const width = useSelector(({ common }) => common.width);
  const [searchText, setSearchText] = useState('');
  const [showRecentAccessedJob, setShowRecentAccessedJob] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [mouseEvent, setMouseEvent] = useState(null);
  const [rect, setRect] = useState(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { authUser, setAuthUser } = useAuth();


  const iconRef = useRef(null);
  const recentJobRef = useRef(null); // 新增：弹框的ref
  const hoverTimer = useRef(null); // 改用useRef存定时器，避免闭包问题
  const closeTimer = useRef(null); // 关闭延迟定时器：桥接图标到浮层的间隙
  const isMobile = width < MOBILE_SIZE; // 手机设备
  const isIPad = width >= MOBILE_SIZE && width < IPAD_SIZE; // iPad设备
  const isDesktop = width >= IPAD_SIZE; // 桌面设备

  const languageMenu = () => (
    <CustomScrollbars className="gx-popover-lang-scroll">
      <ul className="gx-sub-popover">
        {languageData.map(language =>
          <li className="gx-media gx-pointer" key={JSON.stringify(language)} onClick={() =>
            dispatch(switchLanguage(language))
          }>
            <i className={`flag flag-24 gx-mr-2 flag-${language.icon}`} />
            <span className="gx-language-text">{language.name}</span>
          </li>
        )}
      </ul>
    </CustomScrollbars>);

  const onChange = (evt) => {
    setSearchText(evt.target.value);
  };

  const onKeyDown = (evt) => {
    var evt = evt || window.event
    if (evt.keyCode == "13") {
      quickSearch();
    }
  };

  const onSearch = () => {
    quickSearch();
  };

  const quickSearch = () => {
    if (searchText.length >= 3) {
      history.push('/jobs?keyword=' + searchText);
    } else {
      message.info('Please enter 3 characters or more for quick search.');
    }
  };

  // 取消待关闭：鼠标进入图标/浮层时调用
  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // 启动 ~250ms 后关闭：给鼠标从图标移到浮层留出宽限期，桥接两者间隙
  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setShowRecentAccessedJob(false), 250);
  };

  const onMouseEnter = (evt) => {
    clearCloseTimer(); // 重新进入时取消上一次的待关闭，避免误关
    if (hoverTimer.current) clearTimeout(hoverTimer.current);

    hoverTimer.current = setTimeout(() => {
      if (iconRef.current) {
        setRect(iconRef.current.getBoundingClientRect());
        setShowRecentAccessedJob(true);
      }
    }, 300);
  };

  // 离开图标：取消尚未触发的打开计时器，并延迟关闭（鼠标可在宽限期内移入浮层）
  const onIconMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    scheduleClose();
  };

  useEffect(() => {
    if (!isDesktop || !showRecentAccessedJob) return;

    // 监听页面点击：点击其他区域也关闭弹框
    const handleClickOutside = (event) => {
      const iconDom = iconRef.current;
      const jobDom = recentJobRef.current;

      if (iconDom && !iconDom.contains(event.target) && jobDom && !jobDom.contains(event.target)) {
        setShowRecentAccessedJob(false);
      }
    };

    document.addEventListener('click', handleClickOutside);

    // 清理函数：移除监听并清理定时器，避免内存泄漏与误触
    return () => {
      document.removeEventListener('click', handleClickOutside);
      if (hoverTimer.current) clearTimeout(hoverTimer.current);
      clearCloseTimer();
    };
  }, [isDesktop, showRecentAccessedJob]);


  useEffect(() => {
    if (history.location.pathname === '/jobs' && history.location.search.indexOf('keyword') >= 0) {
      const urlParams = new URLSearchParams(history.location.search);
      let keyword = urlParams.get('keyword');
      setSearchText(keyword);
    } else {
      setSearchText('');
    }
    if ((isMobile || isIPad) && showRecentAccessedJob) {
      const handleClickOutside = (event) => {
        const iconElement = iconRef.current;
        const recentJobElement = document.querySelector('.RecentAccessedJobDiv')?.closest('div[style*="z-index: 9999"]');
        if (iconElement &&
          !iconElement.contains(event.target) &&
          recentJobElement &&
          !recentJobElement.contains(event.target)) {
          setShowRecentAccessedJob(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [location, showRecentAccessedJob, isMobile, isIPad]);

  return (
    <>
      <Header>
        {navStyle === NAV_STYLE_DRAWER || ((navStyle === NAV_STYLE_FIXED || navStyle === NAV_STYLE_MINI_SIDEBAR) && width < TAB_SIZE) ?
          <div className={`gx-linebar ${isMobile ? 'gx-mr-1' : 'gx-mr-3'}`}>
            <i className="gx-icon-btn icon icon-menu"
              onClick={() => {
                dispatch(toggleCollapsedSideNav(!navCollapsed));
              }}
            />
          </div> : null}
        {/* {!isMobile && (
          <Link to="/dashboard" className="gx-d-block gx-d-lg-none gx-pointer">
            <img alt="Logo" src={("/assets/images/w-logo.png")} />
          </Link>
        )} */}
        <SearchBox
          styleName="gx-d-none gx-d-lg-block gx-lt-icon-search-bar-lg"
          placeholder="Quick search"
          onChange={onChange}
          onKeyDown={onKeyDown}
          onSearch={onSearch}
          value={searchText} />
        {!isMobile && (
          authUser.webUIConfig?.areaList?.filter(item => item.name === 'Business' && item.module === '')[0]?.pageList?.filter(item => item.name === 'AdvanceSearch')[0] !== undefined ?
            <>
              <Divider type="vertical" />
              <i
                className="fal fa-file-search"
                style={{
                  color: '#6A7CF0',
                  cursor: 'pointer',
                  fontSize: '22px'
                }}
                onClick={() => {
                  setShowAdvancedSearch(true);
                }}
              ></i>
            </> : <></>
        )}
        <Divider type="vertical" />
        <i
          className="fal fa-history"
          style={{
            color: '#6A7CF0',
            cursor: 'pointer',
            fontSize: '22px'
          }}
          {...(!isMobile && !isIPad ? { onMouseEnter: onMouseEnter, onMouseLeave: onIconMouseLeave } : {})}
          {...(isMobile || isIPad ? {
            onTouchStart: () => {
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setRect(rect);
              }
              setShowRecentAccessedJob(!showRecentAccessedJob);
            }
          } : {})}
          onClick={(e) => {
            // 针对iPad桌面模式的兼容性处理
            if (isIPad) {
              e.preventDefault();
              if (iconRef.current) {
                const rect = iconRef.current.getBoundingClientRect();
                setRect(rect);
              }
              setShowRecentAccessedJob(prev => !prev);
            }
          }}
          ref={iconRef}
          role="button"
          tabIndex={0}
        />
        <ul className="gx-header-notifications gx-ml-auto">
          <UserProfile />
        </ul>
      </Header>
      {
        showRecentAccessedJob ? <RecentAccessedJob rect={rect} mouseEvent={mouseEvent} setShowRecentAccessedJob={setShowRecentAccessedJob} jobRef={recentJobRef} onPopupMouseEnter={clearCloseTimer} onPopupMouseLeave={scheduleClose} /> : <></>
      }
      {
        showAdvancedSearch ? <AdvanceSearch history={history} setShowAdvancedSearch={setShowAdvancedSearch} authUser={authUser} /> : <></>
      }
    </>

  );
};

export default Topbar;
